import { useState, type ReactNode } from 'react';

import { DestinationName } from '@/shared/triage/DestinationName';
import {
  CATEGORY_LABELS,
  SOURCE_LABELS,
  type EmailCategory,
  type TriageSource,
} from '@/shared/triage/vocabulary';
import { Button } from '@/shared/ui/Button';
import { Callout } from '@/shared/ui/Callout';
import { Card } from '@/shared/ui/Card';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { HelpPopover } from '@/shared/ui/HelpPopover';
import { Segmented, type SegmentOption } from '@/shared/ui/Segmented';
import { Spinner } from '@/shared/ui/Spinner';
import { Stat } from '@/shared/ui/Stat';
import { TBody, THead, Table, Td, Th, Tr } from '@/shared/ui/Table';
import { RefreshIcon } from '@/shared/ui/icons';
import { formatDate } from '@/shared/utils/format';

import {
  accuracyText,
  accuracyTone,
  confusionGrid,
  formatEur,
  formatSeconds,
  windowFor,
  type CorrectionPair,
  type Period,
  type TriageAccuracy,
  type TriageStats,
} from './types';
import { useTriageStats } from './useStats';

const dateFormat = new Intl.DateTimeFormat('es-ES', { dateStyle: 'medium' });
const timeFormat = new Intl.DateTimeFormat('es-ES', { timeStyle: 'short' });

const PERIODS: SegmentOption<Period>[] = [
  { value: '7', label: '7 días' },
  { value: '30', label: '30 días' },
  { value: 'custom', label: 'Personalizado' },
];

const destination = (key: string) => <DestinationName destination={key} />;
const category = (key: string) => CATEGORY_LABELS[key as EmailCategory] ?? key;

const HELP = [
  <>
    La cifra de acierto sale <strong>solo</strong> de los correos donde alguien pulsó «Correcto» o
    «Corregir a…» en la bandeja. Sin revisiones no hay cifra, y eso no es un 0 %.
  </>,
  <>
    <strong>Propuestas</strong> son las decisiones del sistema; <strong>revisadas</strong>, las que
    una persona miró; <strong>aciertos</strong>, las que la persona confirmó.
  </>,
  <>
    Una decisión «por regla» es fija (una factura con albarán siempre es una factura); una «por
    modelo» la toma la inteligencia artificial. Las reglas no fallan; el modelo es lo que se mide.
  </>,
  <>
    <strong>Lo que más corrige</strong> es la lista para la revisión semanal: cada fila es un tipo
    de error repetido, y el número, cuántas veces. En la matriz, las filas son lo que propuso el
    sistema, las columnas lo que dijo la persona, y la diagonal, los aciertos.
  </>,
  <>
    <strong>Cuánto tarda</strong> va desde que el correo entra hasta que el sistema decide qué es.
    La mediana es lo normal; el percentil 95, lo que tarda uno de cada veinte en el peor caso. El
    objetivo es que casi todo esté decidido en cinco minutos.
  </>,
  <>
    <strong>El coste</strong> es lo que se ha pagado al modelo en el periodo: clasificar correos es
    barato; leer una oferta (un PDF, unas fotos) es la llamada cara. «Correos parecidos» es lo que
    cuesta buscar, entre los ya revisados, los que se parecen al que llega: casi nada, y cero
    mientras esa búsqueda esté apagada.
  </>,
];

/**
 * The figure that closes H3: how often the proposal matched what a person said.
 *
 * Everything here is over the same window and comes computed from the back, rounding
 * included, so this screen and any other one cannot disagree about the same number. The
 * period lives in the title row: it is the one control of the screen, and the numbers
 * start right under it.
 */
/**
 * Administración → Estadísticas (2026-10-02, Eloy): it was a screen of its own in the
 * sidebar and is now a tab of Administración, superadministrators only, like Correcciones
 * and Incidencias. The tab bar is the header: this is the line under it, the period and
 * «Actualizar», and the help of the figures behind its own «?».
 */
export function StatsTab() {
  const [period, setPeriod] = useState<Period>('7');
  const [custom, setCustom] = useState({ from: '', to: '' });
  // Frozen while the period does not change, so a re-render does not move `to` a few
  // milliseconds and refetch on every keystroke of the custom dates.
  const [now, setNow] = useState(() => new Date());
  const window = windowFor(period, custom, now);
  const stats = useTriageStats(window ?? {}, { enabled: window !== null });

  return (
    <>
      <div className="flex flex-col gap-3">
        <div className="text-ink-2 flex items-center gap-2 text-[14px] leading-relaxed">
          Cuánto acierta el sistema, qué corrige más, cuánto tarda y lo que cuesta.
          <HelpPopover content={HELP} />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Segmented
            label="Periodo"
            value={period}
            options={PERIODS}
            onChange={(value) => {
              setPeriod(value);
              setNow(new Date());
            }}
          />
          {period === 'custom' ? (
            <>
              <input
                aria-label="Desde"
                type="date"
                className="field h-8 text-[13.5px]"
                value={custom.from}
                max={custom.to || undefined}
                onChange={(event) => setCustom((c) => ({ ...c, from: event.target.value }))}
              />
              <input
                aria-label="Hasta"
                type="date"
                className="field h-8 text-[13.5px]"
                value={custom.to}
                min={custom.from || undefined}
                onChange={(event) => setCustom((c) => ({ ...c, to: event.target.value }))}
              />
            </>
          ) : null}
          <Button
            variant="ghost"
            size="sm"
            icon={<RefreshIcon size={15} />}
            // A new `to`, so the window moves to now: a mail that arrived since the
            // page was opened is in the refreshed figure, not only the corrections.
            onClick={() => setNow(new Date())}
            disabled={stats.isFetching}
          >
            {stats.isFetching ? 'Actualizando…' : 'Actualizar'}
          </Button>
        </div>
      </div>

      {window === null ? (
        <Callout tone="neutral" compact>
          Elige las dos fechas: el periodo va de la primera a la segunda, ambas incluidas.
        </Callout>
      ) : null}
      {window !== null && stats.isPending ? (
        <Spinner variant="block" label="Cargando estadísticas" />
      ) : null}
      {stats.isError ? (
        <ErrorMessage error={stats.error}>No se pudieron cargar las estadísticas</ErrorMessage>
      ) : null}
      {stats.data ? <TriageStatsView stats={stats.data} /> : null}
    </>
  );
}

function TriageStatsView({ stats }: { stats: TriageStats }) {
  const { overall } = stats;
  const reviewed = overall.reviewed > 0;
  const empty = stats.emails_total === 0;

  return (
    <>
      <section aria-labelledby="resumen" className="flex flex-col gap-3">
        {/* `generated_at` is not decoration: it is what makes a number on screen honest.
            Without it nobody can tell a figure computed a second ago from one computed
            before the six corrections they just made. */}
        <h2 id="resumen" className="muted font-sans font-normal tracking-normal">
          Del {formatDate(dateFormat, stats.window_from)} al{' '}
          {formatDate(dateFormat, stats.window_to)}. Calculado a las{' '}
          {formatDate(timeFormat, stats.generated_at)}.
        </h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Stat
            label="Acierto"
            value={accuracyText(overall)}
            tone={accuracyTone(overall)}
            hint={`${overall.correct} de ${overall.reviewed} revisados`}
          />
          <Stat
            label="Correos recibidos"
            value={String(stats.emails_total)}
            hint={`${stats.emails_with_decision} con decisión`}
          />
          {/* "Procesados sin decisión" y no "Sin decisión": el back solo cuenta los que
              ya pasaron por el worker (max(processed - with_decision, 0)). Los que están
              en `pending` no salen aquí, y llamarlo "sin decisión" invitaba a restar
              cifras que no cuadran. */}
          <Stat
            label="Procesados sin decisión"
            value={String(stats.emails_without_decision)}
            tone={stats.emails_without_decision > 0 ? 'warn' : 'neutral'}
            hint={
              stats.emails_without_decision > 0
                ? 'Pasaron por el sistema y nadie los clasificó'
                : 'Todo lo procesado tiene decisión'
            }
          />
          <Stat
            label="En error"
            value={String(stats.emails_in_error)}
            tone={stats.emails_in_error > 0 ? 'danger' : 'neutral'}
            hint="Se reprocesan desde la bandeja"
          />
        </div>

        {empty ? (
          <Callout tone="info" title="No hay correos en este periodo">
            No entró ningún correo entre esas fechas, así que no hay nada que medir. Prueba con un
            periodo más largo.
          </Callout>
        ) : !reviewed ? (
          <Callout tone="info" title="Todavía no hay cifra de acierto">
            Nadie ha revisado ninguna decisión todavía. Eso no es un 0 %: es una muestra vacía. Se
            llena desde la bandeja, con «Correcto» y «Corregir a…».
          </Callout>
        ) : null}
      </section>

      <Card
        title="Lo que más corrige"
        aside={
          reviewed ? (
            <span className="muted">Sobre {overall.reviewed} decisiones revisadas</span>
          ) : null
        }
      >
        <div className="grid gap-4 lg:grid-cols-2">
          <CorrectionsTable
            caption="Destinos corregidos"
            rows={stats.corrected_destinations ?? []}
            label={destination}
            empty={
              reviewed
                ? 'Ninguna corrección de destino: todo lo revisado estaba bien.'
                : 'Sin revisiones en el periodo.'
            }
          />
          <CorrectionsTable
            caption="Categorías corregidas"
            rows={stats.corrected_categories ?? []}
            label={category}
            empty={
              reviewed
                ? 'Nadie ha cambiado la categoría de ninguna decisión: «Corregir a…» solo la pide si el destino estaba mal por la categoría.'
                : 'Sin revisiones en el periodo.'
            }
          />
        </div>
        <ConfusionMatrix stats={stats} />
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <AccuracyTable caption="Por buzón" rows={stats.by_mailbox} label={(key) => key} />
        <AccuracyTable
          caption="Por categoría"
          rows={stats.by_category}
          label={(key) => CATEGORY_LABELS[key as EmailCategory] ?? key}
        />
        <AccuracyTable
          caption="Por origen"
          rows={stats.by_source}
          label={(key) => SOURCE_LABELS[key as TriageSource] ?? key}
        />
        <AccuracyTable
          caption="Por destino propuesto"
          rows={stats.by_destination}
          label={destination}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Cuánto tarda">
          <div className="grid gap-3 sm:grid-cols-3">
            <Stat
              label="Mediana"
              value={formatSeconds(stats.latency.p50_seconds)}
              hint={
                stats.latency.emails > 0
                  ? `Sobre ${stats.latency.emails} correos con decisión`
                  : 'Sin correos con decisión en el periodo'
              }
            />
            <Stat
              label="Percentil 95"
              value={formatSeconds(stats.latency.p95_seconds)}
              tone={
                stats.latency.p95_seconds == null
                  ? 'neutral'
                  : stats.latency.p95_seconds > 300
                    ? 'warn'
                    : 'ok'
              }
              hint="Objetivo: menos de cinco minutos"
            />
            <Stat
              label="Correos medidos"
              value={String(stats.latency.emails)}
              hint="Con al menos una decisión"
            />
          </div>
        </Card>

        <Card title="Coste del modelo">
          <div className="grid gap-3 sm:grid-cols-2">
            <Stat
              label="Total del periodo"
              value={formatEur(stats.cost_total_eur)}
              hint="Clasificar, leer ofertas y buscar correos parecidos"
            />
            <Stat
              label="Clasificar correos"
              value={formatEur(stats.cost.cost_eur)}
              hint={`${stats.cost.calls} llamadas, ${formatEur(stats.cost.cost_per_email_eur)} por correo`}
            />
            <Stat
              label="Leer ofertas"
              value={formatEur(stats.cost_extraction.cost_eur)}
              hint={`${stats.cost_extraction.calls} lecturas`}
            />
            {/* The vectors of the similar-emails search (contract v10.30). Zero while the
                search is off, which is most of the time today, and then it says so. Absent
                from a back before v10.30: the front and the back deploy at the same time on
                a push, and for a minute the new page can talk to the old API. */}
            {stats.cost_embedding ? (
              <Stat
                label="Correos parecidos"
                value={formatEur(stats.cost_embedding.cost_eur)}
                hint={
                  stats.cost_embedding.calls === 0
                    ? 'Sin búsquedas en el periodo'
                    : `${stats.cost_embedding.calls} ${stats.cost_embedding.calls === 1 ? 'búsqueda' : 'búsquedas'}`
                }
              />
            ) : null}
          </div>
        </Card>
      </div>
    </>
  );
}

function AccuracyTable({
  caption,
  rows,
  label,
}: {
  caption: string;
  rows: TriageAccuracy[];
  label: (key: string) => ReactNode;
}) {
  // The heading is an `h3` whether there are rows or not: putting the title in a
  // `<caption>` when there is data and in an `<h3>` when there is none made the heading
  // outline of the page depend on how busy the week had been.
  return (
    <Card padded={false}>
      <h3 className="text-ink border-line-2 border-b px-4 py-2.5 text-[15px] font-semibold">
        {caption}
      </h3>
      {rows.length === 0 ? (
        <p className="text-ink-3 px-4 py-3 text-[14px]">Sin decisiones en la ventana.</p>
      ) : (
        <Table label={caption} minWidth="24rem">
          <THead>
            <tr>
              <Th className="pl-4">Clave</Th>
              <Th className="text-right whitespace-nowrap">Propuestas</Th>
              <Th className="text-right whitespace-nowrap">Revisadas</Th>
              <Th className="text-right whitespace-nowrap">Aciertos</Th>
              <Th className="pr-4 text-right whitespace-nowrap">Acierto</Th>
            </tr>
          </THead>
          <TBody>
            {rows.map((row) => (
              <Tr key={row.key}>
                <th scope="row" className="text-ink px-4 py-2 text-left font-normal">
                  {label(row.key)}
                </th>
                <Td className="num text-ink-2 whitespace-nowrap">{row.proposed}</Td>
                <Td className="num text-ink-2 whitespace-nowrap">{row.reviewed}</Td>
                <Td className="num text-ink-2 whitespace-nowrap">{row.correct}</Td>
                <Td className="num text-ink pr-4 font-medium whitespace-nowrap">
                  {accuracyText(row)}
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
    </Card>
  );
}

function CorrectionsTable({
  caption,
  rows,
  label,
  empty,
}: {
  caption: string;
  rows: CorrectionPair[];
  label: (key: string) => ReactNode;
  empty: string;
}) {
  return (
    <div>
      <h3 className="text-ink mb-2 text-[14.5px] font-semibold">{caption}</h3>
      {rows.length === 0 ? (
        <p className="text-ink-3 text-[13.5px]">{empty}</p>
      ) : (
        <Table label={caption} minWidth="20rem">
          <THead>
            <tr>
              <Th>El sistema propuso</Th>
              <Th>La persona dijo</Th>
              <Th className="text-right whitespace-nowrap">Veces</Th>
            </tr>
          </THead>
          <TBody>
            {rows.map((row) => (
              <Tr key={`${row.proposed}-${row.final}`}>
                <Td className="text-ink">{label(row.proposed)}</Td>
                <Td className="text-ink">{label(row.final)}</Td>
                <Td className="num text-ink text-right font-medium whitespace-nowrap">
                  {row.count}
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
    </div>
  );
}

/**
 * Proposed destination (rows) against what the person said (columns), over the
 * reviewed decisions. The diagonal is the accuracy; everything off it says in which
 * direction the system errs, which is what decides which rule or prompt to touch.
 */
function ConfusionMatrix({ stats }: { stats: TriageStats }) {
  const grid = confusionGrid(stats.confusion ?? []);
  if (grid.proposed.length === 0) return null;
  return (
    <div>
      <h3 className="text-ink mb-2 text-[14.5px] font-semibold">Matriz: propuesto × final</h3>
      <Table label="Matriz de confusión" minWidth="28rem">
        <THead>
          <tr>
            <Th>Propuesto ↓ / Final →</Th>
            {grid.finals.map((final) => (
              <Th key={final} className="text-right whitespace-nowrap">
                {destination(final)}
              </Th>
            ))}
          </tr>
        </THead>
        <TBody>
          {grid.proposed.map((proposed) => (
            <Tr key={proposed}>
              <th scope="row" className="text-ink px-3 py-2 text-left font-normal">
                {destination(proposed)}
              </th>
              {grid.finals.map((final) => {
                const count = grid.count(proposed, final);
                const hit = proposed === final;
                return (
                  <Td
                    key={final}
                    className={
                      count === 0
                        ? 'num text-ink-4 text-right'
                        : hit
                          ? 'num text-ok text-right font-medium'
                          : 'num text-warn text-right font-medium'
                    }
                  >
                    {count}
                  </Td>
                );
              })}
            </Tr>
          ))}
        </TBody>
      </Table>
    </div>
  );
}
